import {
  ConditionGroup,
  Meeting,
  MeetingAttachableType,
  MeetingConsent,
  MeetingConsentAttestedStatus,
  MeetingConsentResponse,
  MeetingPrivacy,
  MuxVideoTokenResponse,
  NextPageParams,
  PaginationMetadata,
} from "@/schema"

import { ApiContext, apiContextBlobFetch, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

import { TasksResponse } from "../tasks/api"

// Meeting types
export interface MeetingsResponse {
  meetings: Meeting[]
  pagination?: PaginationMetadata
}

export interface MeetingsListResponse {
  meetings: Meeting[]
  pagination: PaginationMetadata
}

// Meeting API functions
export function fetchMeetings(
  context: ApiContext,
  options?: {
    startDate?: Date
    endDate?: Date
    scope?: "upcoming" | "upcoming_or_current" | "recently_completed" | "today" | "processed"
    limit?: number
    userIds?: string[]
    feed?: boolean
    pageParams?: NextPageParams
    conditionGroup?: ConditionGroup
    search?: string
  }
): Promise<MeetingsResponse> {
  const params = new URLSearchParams()
  if (options?.startDate) params.append("start_date", options.startDate.toISOString().split("T")[0])
  if (options?.endDate) params.append("end_date", options.endDate.toISOString().split("T")[0])
  if (options?.scope) params.append("scope", options.scope)
  if (options?.limit) params.append("limit", String(options.limit))
  if (options?.feed) params.append("feed", "true")
  if (options?.pageParams) {
    params.append("page", options.pageParams.page.toString())
    params.append("per_page", options.pageParams.per_page.toString())
  }
  if (options?.userIds?.length) {
    options.userIds.forEach((id) => params.append("user_ids[]", id))
  }
  if (options?.search) params.append("search", options.search)
  if (options?.conditionGroup) {
    appendConditionGroupParams(params, options.conditionGroup)
  }
  const queryString = params.toString() ? `?${params.toString()}` : ""
  return apiContextFetch<MeetingsResponse>(context, `meetings${queryString}`)
}

export function fetchMeetingsList(
  context: ApiContext,
  conditionGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
): Promise<MeetingsListResponse> {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  } else {
    params.append("page", "1")
    params.append("per_page", "50")
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (conditionGroup) {
    appendConditionGroupParams(params, conditionGroup)
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<MeetingsListResponse>(context, `meetings/table_index?${params.toString()}`)
}

export function fetchHouseholdMeetings(
  context: ApiContext,
  householdId: string,
  options?: { scope?: "past" | "future" | "preview"; since?: string; limit?: number }
): Promise<MeetingsResponse> {
  const params = new URLSearchParams()
  if (options?.scope) params.append("scope", options.scope)
  if (options?.since) params.append("since", options.since)
  if (options?.limit) params.append("limit", String(options.limit))
  const queryString = params.toString() ? `?${params.toString()}` : ""
  return apiContextFetch<MeetingsResponse>(context, `households/${householdId}/meetings${queryString}`)
}

export function fetchContactMeetings(context: ApiContext, contactId: string): Promise<MeetingsResponse> {
  return apiContextFetch<MeetingsResponse>(context, `meetings?contact_id=${contactId}`)
}

export function fetchMeetingPreview(
  context: ApiContext,
  type: MeetingAttachableType,
  id: string
): Promise<MeetingsResponse> {
  if (type === "household") return fetchHouseholdMeetings(context, id, { scope: "preview" })
  const params = new URLSearchParams({ scope: "preview", [`${type}_id`]: id })
  return apiContextFetch<MeetingsResponse>(context, `meetings?${params.toString()}`)
}

export function fetchBusinessMeetings(context: ApiContext, businessId: string): Promise<MeetingsResponse> {
  return apiContextFetch<MeetingsResponse>(context, `meetings?business_id=${businessId}`)
}

export function fetchMeeting(context: ApiContext, meetingId: string): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}`)
}

export async function fetchMeetingPrivacy(context: ApiContext, meetingId: string): Promise<MeetingPrivacy> {
  const response = await apiContextFetch<{ privacy: MeetingPrivacy }>(context, `meetings/${meetingId}/privacy`)
  return response.privacy
}

export async function updateMeetingPrivacy(
  context: ApiContext,
  meetingId: string,
  excludedUserIds: string[]
): Promise<MeetingPrivacy> {
  const response = await apiContextFetch<{ privacy: MeetingPrivacy }>(context, `meetings/${meetingId}/privacy`, {
    method: "PATCH",
    body: JSON.stringify({ privacy: { excluded_user_ids: excludedUserIds } }),
  })
  return response.privacy
}

export async function attestMeetingConsent(
  context: ApiContext,
  meetingId: string,
  attestedStatus: MeetingConsentAttestedStatus
): Promise<MeetingConsent> {
  const response = await apiContextFetch<MeetingConsentResponse>(context, `meetings/${meetingId}/consent`, {
    method: "PATCH",
    body: JSON.stringify({ consent: { attested_status: attestedStatus } }),
  })
  return response.meeting_consent
}

export function updateMeeting(
  context: ApiContext,
  meetingId: string,
  data: Partial<Meeting> & { household_ids?: string[]; user_ids?: string[]; meeting_topic_ids?: string[] }
): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}`, {
    method: "PUT",
    body: JSON.stringify({ meeting: data }),
  })
}

export function deleteMeeting(context: ApiContext, meetingId: string): Promise<void> {
  return apiContextFetch<void>(context, `meetings/${meetingId}`, {
    method: "DELETE",
  })
}

export async function updateMeetingWithFiles(
  context: ApiContext,
  meetingId: string,
  newFiles: File[],
  existingSignedIds: string[] = []
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  const allSignedIds = [...existingSignedIds, ...newSignedIds]

  return apiContextFetch<Meeting>(context, `meetings/${meetingId}`, {
    method: "PUT",
    body: JSON.stringify({ meeting: { files: allSignedIds } }),
  })
}

export async function fetchMuxVideoToken(context: ApiContext, meetingId: string): Promise<MuxVideoTokenResponse> {
  return await apiContextFetch<MuxVideoTokenResponse>(context, `meetings/${meetingId}/mux_video_token`)
}

export async function fetchMeetingTasks(context: ApiContext, meetingId: string): Promise<TasksResponse> {
  return await apiContextFetch<TasksResponse>(context, `meetings/${meetingId}/tasks`)
}

export async function createMeetingNotetaker(context: ApiContext, meetingId: string): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}/notetaker`, {
    method: "POST",
  })
}

export async function deleteMeetingNotetaker(context: ApiContext, meetingId: string): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}/notetaker`, {
    method: "DELETE",
  })
}

export async function sendLiveBot(context: ApiContext, meetingId: string): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}/send_live_bot`, {
    method: "POST",
  })
}

export async function createInstantMeeting(
  context: ApiContext,
  householdId: string,
  data: { meeting_link?: string; title?: string }
): Promise<Meeting> {
  return apiContextFetch<Meeting>(context, `households/${householdId}/meetings`, {
    method: "POST",
    body: JSON.stringify({ meeting: data }),
  })
}

export async function generateReviewMaterials(
  context: ApiContext,
  meetingId: string,
  { force }: { force?: boolean } = {}
): Promise<Meeting> {
  const params = force ? "?force=true" : ""
  return apiContextFetch<Meeting>(context, `meetings/${meetingId}/generate_review_materials${params}`, {
    method: "POST",
  })
}

export async function createRecordingTranscription(
  context: ApiContext,
  meetingId: string,
  { speakers, additionalInstructions }: { speakers?: string[]; additionalInstructions?: string } = {}
): Promise<{ recording_status: "processing" }> {
  const data = {
    ...(speakers ? { speakers } : {}),
    ...(additionalInstructions ? { additional_instructions: additionalInstructions } : {}),
  }

  return apiContextFetch<{ recording_status: "processing" }>(context, `meetings/${meetingId}/recording_transcription`, {
    method: "POST",
    body: Object.keys(data).length ? JSON.stringify({ recording_transcription: data }) : undefined,
  })
}

export async function downloadMeetingTranscript(context: ApiContext, meetingId: string): Promise<Blob> {
  return apiContextBlobFetch(context, `meetings/${meetingId}/download_transcript`)
}

export async function fetchMeetingVideoDownloadUrl(
  context: ApiContext,
  meetingId: string
): Promise<{ download_url: string }> {
  return apiContextFetch<{ download_url: string }>(context, `meetings/${meetingId}/download_mux_video`)
}

export async function fetchMeetingAudioDownloadUrl(
  context: ApiContext,
  meetingId: string
): Promise<{ download_url: string }> {
  return apiContextFetch<{ download_url: string }>(context, `meetings/${meetingId}/download_audio`)
}

export async function createMeetingTranscript(
  context: ApiContext,
  meetingId: string,
  data: { user_upload_text: string }
): Promise<{ message: string }> {
  return apiContextFetch<{ message: string }>(context, `meetings/${meetingId}/transcript`, {
    method: "POST",
    body: JSON.stringify({ transcript: data }),
  })
}

export async function savePrepAsNotes(context: ApiContext, meetingId: string): Promise<{ notes_created: number }> {
  return apiContextFetch<{ notes_created: number }>(context, `meetings/${meetingId}/save_prep_as_notes`, {
    method: "POST",
  })
}

export async function updateTranscriptSpeaker(
  context: ApiContext,
  meetingId: string,
  speakerId: string,
  data: { overridden_name: string | null }
): Promise<void> {
  return apiContextFetch<void>(context, `meetings/${meetingId}/transcript_speakers/${speakerId}`, {
    method: "PUT",
    body: JSON.stringify({ transcript_speaker: data }),
  })
}

export interface FindAndReplaceParams {
  find: string
  replace_with: string
  case_sensitive: boolean
}

export interface FindAndReplaceResponse {
  replacement_count: number
}

export async function findAndReplace(
  context: ApiContext,
  meetingId: string,
  data: FindAndReplaceParams
): Promise<FindAndReplaceResponse> {
  return apiContextFetch<FindAndReplaceResponse>(context, `meetings/${meetingId}/find_and_replace`, {
    method: "POST",
    body: JSON.stringify({ meeting: { find_and_replace: data } }),
  })
}
